import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Card,
  Alert,
  SimpleGrid,
  useDisclosure,
  Flex,
  Icon,
  Spinner,
  IconButton,
  Badge,
  ButtonGroup,
  Input,
  NativeSelect,
  Table,
  Tabs,
  Dialog,
  Portal,
  Field,
} from '@chakra-ui/react';
import { Thead, Tbody, Tr, Th, Td, TabList, Tab, TabPanels, TabPanel, tabsControlledValue, tabsOnValueChange } from '@/components/ui/chakra-v2-compat';
import { toaster, toast } from '@/components/ui/toaster';
import {
  FaPlus,
  FaTrash,
  FaThList,
  FaCalendarAlt,
  FaUsers,
  FaUserPlus,
  FaMusic,
  FaMapMarkerAlt,
  FaTh,
  FaFilter,
  FaTimes,
  FaHistory,
} from 'react-icons/fa';
import AppLayout from './AppLayout.jsx';
import ActuacionDetalle from './ActuacionDetalle.jsx';
import ActuacionCreateWizard from './ActuacionCreateWizard.jsx';
import { actuacionesAPI, formacionesAPI, musicosAPI } from '../services/api';
import { formatActuacionHorario, labelTipoActuacion, colorTipoActuacion } from '../constants/tipoActuacion.js';
import { useTiposFormacionCatalogo } from '../hooks/useTiposFormacionCatalogo.js';
import { isActuacionPast } from '../utils/actuacionTemporal.js';
import { useProductTour } from '../hooks/useProductTour';
import { bandaActuacionesSteps } from '../tours/bandaActuacionesTour';

const ESTADO_LABELS = {
  BORRADOR: 'Borrador',
  ENCUESTA_ABIERTA: 'Encuesta abierta',
  EN_PREPARACION: 'En preparación',
  LISTA: 'Lista',
  ARCHIVADA: 'Archivada',
};
const ESTADO_COLORS = {
  BORRADOR: 'gray',
  ENCUESTA_ABIERTA: 'yellow',
  EN_PREPARACION: 'blue',
  LISTA: 'green',
  ARCHIVADA: 'red',
};

const VIEW_STORAGE_KEY = 'cadencya-actuaciones-view';

function actuacionFechaMs(a) {
  if (a?.fecha == null || a.fecha === '') return null;
  const d = new Date(a.fecha);
  // Si `fecha` viene solo con día (sin hora), añadimos `horaInicio` para ordenar correctamente
  // actuaciones del mismo día por su hora real (p. ej. 11:30 antes que 13:30).
  const fechaStr = String(a.fecha || '');
  const hasTimeInFecha = /T\d{2}:\d{2}/.test(fechaStr) || /\d{2}:\d{2}/.test(fechaStr);
  const hi = a?.horaInicio != null && String(a.horaInicio).trim() ? String(a.horaInicio).trim() : '';
  // Hay backends que serializan una "fecha sin hora" como ISO a medianoche.
  // Si la hora en `fecha` es 00:00 y tenemos `horaInicio`, usamos la hora real.
  const fechaIsMidnight =
    Number.isFinite(d.getTime()) && d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0;
  if (hi && (!hasTimeInFecha || fechaIsMidnight)) {
    const m = /^(\d{1,2})(?::(\d{2}))?/.exec(hi);
    if (m) {
      const hh = Math.min(23, Math.max(0, Number(m[1] || 0)));
      const mm = Math.min(59, Math.max(0, Number(m[2] || 0)));
      d.setHours(hh, mm, 0, 0);
    }
  }
  const t = d.getTime();
  return Number.isNaN(t) ? null : t;
}

function startOfDayMs(isoDateStr) {
  const x = new Date(isoDateStr);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
}

function endOfDayMs(isoDateStr) {
  const x = new Date(isoDateStr);
  x.setHours(23, 59, 59, 999);
  return x.getTime();
}

/** Fecha local de hoy en YYYY-MM-DD (para `min` del input type="date"). */
function localDateYMD(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Inicio del día local para un valor YYYY-MM-DD del input date. */
function startOfLocalDayFromYMD(ymd) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(String(ymd).trim())) return null;
  const [y, mo, da] = String(ymd).trim().split('-').map(Number);
  const x = new Date(y, mo - 1, da);
  x.setHours(0, 0, 0, 0);
  const t = x.getTime();
  return Number.isNaN(t) ? null : t;
}

function emptyActuacionForm(plantillas) {
  return {
    nombre: '',
    descripcion: '',
    fecha: '',
    horaInicio: '',
    horaFin: '',
    ubicacion: '',
    lat: null,
    lng: null,
    ubicacionFin: '',
    latFin: null,
    lngFin: null,
    uniforme: '',
    tipo: 'PROCESION',
    plantillaId: plantillas[0]?.id || '',
    enlacePartituras: '',
    observaciones: '',
    necesidadAutobus: false,
    autobusUbicacion: '',
    autobusHoraSalida: '',
    autobusLat: null,
    autobusLng: null,
  };
}

export default function Actuaciones() {
  const { tipos: tiposFormacionCatalogo } = useTiposFormacionCatalogo();
  useProductTour('banda-actuaciones', bandaActuacionesSteps, { autoStart: false });
  const [actuaciones, setActuaciones] = useState([]);
  const [plantillas, setPlantillas] = useState([]);
  const [musicos, setMusicos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const { open: isOpen, onOpen, onClose } = useDisclosure();
  const [form, setForm] = useState(() => emptyActuacionForm([]));
  const [ubicLlegadaDistinta, setUbicLlegadaDistinta] = useState(false);
  const [createFormKey, setCreateFormKey] = useState(0);
  const [viewMode, setViewMode] = useState(() => {
    try {
      const v = localStorage.getItem(VIEW_STORAGE_KEY);
      if (v === 'list' || v === 'grid') return v;
    } catch {
      /* ignore */
    }
    return 'grid';
  });
  const [filterNombre, setFilterNombre] = useState('');
  const [filterFechaDesde, setFilterFechaDesde] = useState('');
  const [filterFechaHasta, setFilterFechaHasta] = useState('');
  const [sortBy, setSortBy] = useState('fecha_asc');
  /** 0 = en curso, 1 = histórico (solo lista, solo lectura al abrir detalle). */
  const [listadoMainTab, setListadoMainTab] = useState(0);
  const [detalleReadOnly, setDetalleReadOnly] = useState(false);
  
  const cancelDeleteRef = useRef();
  const { open: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [actuacionPendienteEliminar, setActuacionPendienteEliminar] = useState(null);
  const [deletingActuacion, setDeletingActuacion] = useState(false);
  const cardBg = useColorModeValue('white', 'gray.800');
  const toolbarBg = useColorModeValue('gray.50', 'gray.900');
  const borderSubtle = useColorModeValue('gray.200', 'gray.600');
  const tableRowHoverBg = useColorModeValue('purple.50', 'whiteAlpha.100');
  const missingMusicos = !loading && (musicos?.length ?? 0) === 0;
  const missingFormaciones = !loading && (plantillas?.length ?? 0) === 0;
  const missingPrereqs = missingMusicos || missingFormaciones;

  useEffect(() => {
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, viewMode);
    } catch {
      /* ignore */
    }
  }, [viewMode]);

  useEffect(() => {
    setFilterNombre('');
    setFilterFechaDesde('');
    setFilterFechaHasta('');
    setSortBy(listadoMainTab === 0 ? 'fecha_asc' : 'fecha_desc');
  }, [listadoMainTab]);

  const actuacionesActivas = useMemo(
    () => actuaciones.filter((a) => !isActuacionPast(a)),
    [actuaciones],
  );
  const actuacionesHistorico = useMemo(
    () => actuaciones.filter((a) => isActuacionPast(a)),
    [actuaciones],
  );

  const applyFiltersAndSort = useCallback((source) => {
    let list = [...source];
    const q = filterNombre.trim().toLowerCase();
    if (q) {
      list = list.filter((a) => (a.nombre || '').toLowerCase().includes(q));
    }
    if (filterFechaDesde) {
      const from = startOfDayMs(filterFechaDesde);
      list = list.filter((a) => {
        const t = actuacionFechaMs(a);
        return t != null && t >= from;
      });
    }
    if (filterFechaHasta) {
      const to = endOfDayMs(filterFechaHasta);
      list = list.filter((a) => {
        const t = actuacionFechaMs(a);
        return t != null && t <= to;
      });
    }

    const nullsLast = (ta, tb) => {
      if (ta == null && tb == null) return 0;
      if (ta == null) return 1;
      if (tb == null) return -1;
      return 0;
    };

    list.sort((a, b) => {
      switch (sortBy) {
        case 'fecha_desc': {
          const ta = actuacionFechaMs(a);
          const tb = actuacionFechaMs(b);
          const n = nullsLast(ta, tb);
          if (n !== 0) return n;
          return tb - ta;
        }
        case 'nombre_asc':
          return String(a.nombre || '').localeCompare(String(b.nombre || ''), 'es', { sensitivity: 'base' });
        case 'nombre_desc':
          return String(b.nombre || '').localeCompare(String(a.nombre || ''), 'es', { sensitivity: 'base' });
        case 'updated_desc': {
          const ua = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
          const ub = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
          return ub - ua;
        }
        case 'fecha_asc':
        default: {
          const ta = actuacionFechaMs(a);
          const tb = actuacionFechaMs(b);
          const n = nullsLast(ta, tb);
          if (n !== 0) return n;
          return ta - tb;
        }
      }
    });
    return list;
  }, [filterNombre, filterFechaDesde, filterFechaHasta, sortBy]);

  const actuacionesActivasFiltradas = useMemo(
    () => applyFiltersAndSort(actuacionesActivas),
    [actuacionesActivas, applyFiltersAndSort],
  );
  const actuacionesHistoricoFiltradas = useMemo(
    () => applyFiltersAndSort(actuacionesHistorico),
    [actuacionesHistorico, applyFiltersAndSort],
  );

  const defaultSortForTab = listadoMainTab === 0 ? 'fecha_asc' : 'fecha_desc';
  const listFiltersActive = Boolean(
    filterNombre.trim() || filterFechaDesde || filterFechaHasta || sortBy !== defaultSortForTab,
  );

  const clearListFilters = () => {
    setFilterNombre('');
    setFilterFechaDesde('');
    setFilterFechaHasta('');
    setSortBy(listadoMainTab === 0 ? 'fecha_asc' : 'fecha_desc');
  };

  const listToolbarSourceLen = listadoMainTab === 0 ? actuacionesActivas.length : actuacionesHistorico.length;
  const listToolbarFilteredLen =
    listadoMainTab === 0 ? actuacionesActivasFiltradas.length : actuacionesHistoricoFiltradas.length;

  const displayedActivas = actuacionesActivasFiltradas;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [aRes, pRes, mRes] = await Promise.all([
        actuacionesAPI.getAll(),
        formacionesAPI.getPlantillas(),
        musicosAPI.getAll().catch(() => ({ data: [] })),
      ]);
      setActuaciones(aRes.data);
      setPlantillas(pRes.data);
      setMusicos(Array.isArray(mRes.data) ? mRes.data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreateModal = () => {
    if (missingPrereqs) {
      toaster.create({
        title: 'Antes de crear actuaciones…',
        description: 'Necesitas añadir músicos y crear al menos una formación.',
        type: 'info',
        duration: 5000,
        closable: true,
      });
      return;
    }
    setForm(emptyActuacionForm(plantillas));
    setUbicLlegadaDistinta(false);
    setCreateFormKey((k) => k + 1);
    onOpen();
  };

  const handleCreateSuccess = (newId) => {
    setListadoMainTab(0);
    setDetalleReadOnly(false);
    setSelectedId(newId);
    fetchData();
  };

  const requestDeleteActuacion = (id) => {
    const actuacion = actuaciones.find((a) => a.id === id);
    setActuacionPendienteEliminar(actuacion || { id, nombre: 'esta actuación' });
    onDeleteOpen();
  };

  const confirmDeleteActuacion = async () => {
    if (!actuacionPendienteEliminar?.id) return;
    setDeletingActuacion(true);
    try {
      await actuacionesAPI.delete(actuacionPendienteEliminar.id);
      toaster.create({ title: 'Actuación eliminada', type: 'info' });
      if (selectedId === actuacionPendienteEliminar.id) setSelectedId(null);
      await fetchData();
      onDeleteClose();
      setActuacionPendienteEliminar(null);
    } catch {
      toaster.create({ title: 'Error al eliminar', type: 'error' });
    } finally {
      setDeletingActuacion(false);
    }
  };

  if (selectedId) {
    return (
      <AppLayout userType="banda">
        <ActuacionDetalle
          actuacionId={selectedId}
          onBack={() => {
            setSelectedId(null);
            setDetalleReadOnly(false);
            fetchData();
          }}
          toast={toast}
          plantillas={plantillas}
          hayMusicos={musicos.length > 0}
          readOnly={detalleReadOnly}
        />
      </AppLayout>
    );
  }

  return (
    <AppLayout userType="banda">
      <Box maxW="1200px" mx="auto" px={{ base: 2, md: 4 }} py={{ base: 4, md: 6 }}>
        <VStack gap={6} align="stretch">
          {missingPrereqs ? (
            <Alert.Root status="warning" variant="subtle" borderRadius="xl" data-tour="actuaciones-prereq">
              <Alert.Indicator />
              <Box>
                <Text fontWeight="800">
                  Para empezar a crear actuaciones necesitas preparar tu banda
                </Text>
                <Text fontSize="sm">
                  {missingMusicos ? 'Añade músicos' : 'Los músicos están listos'}{missingMusicos && missingFormaciones ? ' y ' : missingFormaciones ? ' ' : ''}
                  {missingFormaciones ? 'crea una primera formación' : 'las formaciones están listas'}.
                  {' '}Después podrás gestionar asistencia, refuerzos y formaciones por actuación.
                </Text>
              </Box>
            </Alert.Root>
          ) : null}
          <Flex justify="space-between" align="center" wrap="wrap" gap={3} data-tour="actuaciones-header">
            <HStack>
              <Icon color="purple.500" boxSize={6} asChild><FaThList /></Icon>
              <Heading size="lg">Actuaciones</Heading>
            </HStack>
            <HStack flexWrap="wrap" gap={2}>
              {!loading && actuaciones.length > 0 && listadoMainTab === 0 ? (
                <ButtonGroup attached size="sm" variant="outline">
                  <IconButton
                    aria-label="Vista en mosaico"
                    colorPalette="purple"
                    variant={viewMode === 'grid' ? 'solid' : 'outline'}
                    onClick={() => setViewMode('grid')}><FaTh /></IconButton>
                  <IconButton
                    aria-label="Vista en lista"
                    colorPalette="purple"
                    variant={viewMode === 'list' ? 'solid' : 'outline'}
                    onClick={() => setViewMode('list')}><FaThList /></IconButton>
                </ButtonGroup>
              ) : null}
              {listadoMainTab === 0 ? (
                <Button
                  colorPalette="purple"
                  onClick={openCreateModal}
                  disabled={missingPrereqs}
                  data-tour="actuaciones-nueva"><FaPlus />Nueva actuación
                                  </Button>
              ) : null}
            </HStack>
          </Flex>

          {!loading && listToolbarSourceLen > 0 ? (
            <Box
              bg={toolbarBg}
              borderWidth="1px"
              borderColor={borderSubtle}
              rounded="xl"
              p={4}
            >
              <HStack gap={2} mb={3} color="gray.600">
                <Icon asChild><FaFilter /></Icon>
                <Text fontSize="sm" fontWeight="600">
                  Filtros y orden
                </Text>
              </HStack>
              <Flex direction={{ base: 'column', lg: 'row' }} gap={4} align={{ lg: 'flex-end' }} wrap="wrap">
                <Field.Root flex="1" minW={{ base: 'full', md: '200px' }} maxW={{ lg: '280px' }}>
                  <Field.Label fontSize="xs" mb={1} fontWeight="600">
                    Nombre
                  </Field.Label>
                  <Input
                    size="sm"
                    placeholder="Buscar…"
                    value={filterNombre}
                    onChange={(e) => setFilterNombre(e.target.value)}
                  />
                </Field.Root>
                <Field.Root w={{ base: 'full', md: 'auto' }} maxW={{ md: '180px' }}>
                  <Field.Label fontSize="xs" mb={1} fontWeight="600">
                    Fecha desde
                  </Field.Label>
                  <Input
                    type="date"
                    size="sm"
                    value={filterFechaDesde}
                    onChange={(e) => setFilterFechaDesde(e.target.value)}
                  />
                </Field.Root>
                <Field.Root w={{ base: 'full', md: 'auto' }} maxW={{ md: '180px' }}>
                  <Field.Label fontSize="xs" mb={1} fontWeight="600">
                    Fecha hasta
                  </Field.Label>
                  <Input
                    type="date"
                    size="sm"
                    value={filterFechaHasta}
                    onChange={(e) => setFilterFechaHasta(e.target.value)}
                  />
                </Field.Root>
                <Field.Root flex="1" minW={{ base: 'full', md: '220px' }} maxW={{ lg: '320px' }}>
                  <Field.Label fontSize="xs" mb={1} fontWeight="600">
                    Ordenar por
                  </Field.Label>
                  <NativeSelect.Root>
                    <NativeSelect.Field size="sm" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                      <option value="fecha_asc">Fecha: más próximas primero</option>
                      <option value="fecha_desc">Fecha: más lejanas primero</option>
                      <option value="nombre_asc">Nombre (A → Z)</option>
                      <option value="nombre_desc">Nombre (Z → A)</option>
                      <option value="updated_desc">Última modificación</option>
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Field.Root>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={clearListFilters}
                  disabled={!listFiltersActive}
                  alignSelf={{ base: 'stretch', lg: 'center' }}><FaTimes />Limpiar
                                  </Button>
              </Flex>
              <Text fontSize="xs" color="gray.500" mt={3}>
                Mostrando {listToolbarFilteredLen} de {listToolbarSourceLen}{' '}
                {listToolbarSourceLen === 1 ? 'actuación' : 'actuaciones'}
              </Text>
            </Box>
          ) : null}

          {loading ? (
            <Flex justify="center" py={12}>
              <Spinner size="xl" color="purple.500" />
            </Flex>
          ) : actuaciones.length === 0 ? (
            <Card.Root bg={cardBg} shadow="lg" rounded="2xl">
              <Card.Body textAlign="center" py={12}>
                <Icon boxSize={12} color="gray.300" mb={4} asChild><FaMusic /></Icon>
                <Heading size="md" color="gray.500" mb={2}>
                  No hay actuaciones
                </Heading>
                <Text color="gray.400" mb={4}>
                  {missingPrereqs
                    ? 'Primero añade músicos y crea una formación para poder empezar a crear actuaciones.'
                    : 'Crea tu primera actuación para gestionar asistencia, refuerzos y formaciones.'}
                </Text>
                <Button colorPalette="purple" onClick={openCreateModal} disabled={missingPrereqs}><FaPlus />Crear actuación
                                  </Button>
              </Card.Body>
            </Card.Root>
          ) : (
            <Tabs.Root
              colorPalette="purple"
              variant='subtle'
              size="sm"
              value={tabsControlledValue(listadoMainTab)}
              onValueChange={tabsOnValueChange((i) => setListadoMainTab(i))}
            >
              <Tabs.List flexWrap="wrap" gap={2} data-tour="actuaciones-tabs">
                <Tab value="0" fontWeight="600">
                  En curso ({actuacionesActivas.length})
                </Tab>
                <Tab value="1" fontWeight="600">
                  <HStack gap={2}>
                    <Icon asChild><FaHistory /></Icon>
                    <Text as="span">Histórico ({actuacionesHistorico.length})</Text>
                  </HStack>
                </Tab>
              </Tabs.List>
              <TabPanels>
                <TabPanel value="0" px={0} pt={4}>
                  {actuacionesActivas.length === 0 ? (
                    <Card.Root bg={cardBg} shadow="md" rounded="xl" borderWidth="1px" borderColor={borderSubtle}>
                      <Card.Body py={10} textAlign="center">
                        <Icon boxSize={10} color="gray.400" mb={3} asChild><FaCalendarAlt /></Icon>
                        <Heading size="sm" color="gray.600" mb={2}>
                          No hay actuaciones en curso
                        </Heading>
                        <Text fontSize="sm" color="gray.500" mb={4}>
                          Las actuaciones con fecha y hora ya pasadas pasan al histórico. Las que no tienen fecha siguen
                          aquí hasta que les asignes una.
                        </Text>
                        <Button colorPalette="purple" size="sm" onClick={openCreateModal}><FaPlus />Nueva actuación
                                                  </Button>
                      </Card.Body>
                    </Card.Root>
                  ) : displayedActivas.length === 0 ? (
                    <Card.Root bg={cardBg} shadow="md" rounded="xl" borderWidth="1px" borderColor={borderSubtle}>
                      <Card.Body textAlign="center" py={10}>
                        <Icon boxSize={10} color="gray.400" mb={3} asChild><FaFilter /></Icon>
                        <Heading size="sm" color="gray.600" mb={2}>
                          Ninguna actuación coincide con los filtros
                        </Heading>
                        <Text fontSize="sm" color="gray.500" mb={4}>
                          Ajusta el nombre, las fechas o el orden, o limpia los filtros.
                        </Text>
                        <Button size="sm" variant="outline" onClick={clearListFilters}><FaTimes />Limpiar filtros
                                                  </Button>
                      </Card.Body>
                    </Card.Root>
                  ) : viewMode === 'grid' ? (
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={4}>
                      {displayedActivas.map((a) => {
                        const respuestas = a.encuesta?._count?.respuestas || 0;
                        return (
                          <Card.Root
                            key={a.id}
                            bg={cardBg}
                            shadow="md"
                            rounded="xl"
                            overflow="hidden"
                            cursor="pointer"
                            _hover={{ shadow: 'xl', transform: 'translateY(-3px)' }}
                            transition="all 0.2s"
                            onClick={() => {
                              setDetalleReadOnly(false);
                              setSelectedId(a.id);
                            }}
                          >
                            {/* ── Hero: cartel o degradado placeholder ── */}
                            <Box position="relative">
                              {a.cartelUrl ? (
                                <Box
                                  h="160px"
                                  bgImage={`url(${a.cartelUrl})`}
                                  bgSize="cover"
                                  bgPosition="center"
                                />
                              ) : (
                                <Flex
                                  h="80px"
                                  bgGradient="linear(135deg, purple.700, purple.500, purple.300)"
                                  align="center"
                                  justify="center"
                                >
                                  <Icon boxSize={8} color="whiteAlpha.300" asChild><FaMusic /></Icon>
                                </Flex>
                              )}
                              {/* Gradiente inferior con badges */}
                              <Box
                                position="absolute"
                                bottom={0} left={0} right={0}
                                bgGradient={
                                  a.cartelUrl
                                    ? 'linear(to-t, blackAlpha.800, blackAlpha.400, transparent)'
                                    : 'linear(to-t, blackAlpha.600, transparent)'
                                }
                                pt={a.cartelUrl ? 10 : 4}
                                pb={2}
                                px={3}
                              >
                                <HStack gap={1} flexWrap="wrap">
                                  <Badge
                                    colorPalette={ESTADO_COLORS[a.estado]}
                                    variant="solid"
                                    fontSize="2xs"
                                    textTransform="none"
                                  >
                                    {ESTADO_LABELS[a.estado]}
                                  </Badge>
                                  <Badge
                                    colorPalette={colorTipoActuacion(a.tipo, tiposFormacionCatalogo)}
                                    variant="solid"
                                    fontSize="2xs"
                                    textTransform="none"
                                  >
                                    {labelTipoActuacion(a.tipo, tiposFormacionCatalogo)}
                                  </Badge>
                                </HStack>
                              </Box>
                            </Box>

                            {/* ── Info ── */}
                            <Card.Body pt={3} pb={3}>
                              <Heading size="sm" lineClamp={2} mb={1}>
                                {a.nombre}
                              </Heading>
                              {a.fecha && (
                                <HStack gap={1} mb={1}>
                                  <Icon color="purple.400" boxSize={3} asChild><FaCalendarAlt /></Icon>
                                  <Text fontSize="xs" color="gray.500">
                                    {new Date(a.fecha).toLocaleDateString()}
                                    {formatActuacionHorario(a) ? ` · ${formatActuacionHorario(a)}` : ''}
                                  </Text>
                                </HStack>
                              )}
                              {a.ubicacion && (
                                <HStack gap={1} mb={1}>
                                  <Icon color="gray.400" boxSize={3} asChild><FaMapMarkerAlt /></Icon>
                                  <Text fontSize="xs" color="gray.500" lineClamp={1}>
                                    {a.ubicacion}
                                  </Text>
                                </HStack>
                              )}
                              <HStack mt={2} gap={3} color="gray.400" fontSize="xs" justify="space-between">
                                <HStack gap={3}>
                                  {a.encuesta && (
                                    <HStack gap={1}>
                                      <Icon asChild><FaUsers /></Icon>
                                      <Text>{respuestas}</Text>
                                    </HStack>
                                  )}
                                  {a._count?.refuerzos > 0 && (
                                    <HStack gap={1}>
                                      <Icon asChild><FaUserPlus /></Icon>
                                      <Text>{a._count.refuerzos}</Text>
                                    </HStack>
                                  )}
                                </HStack>
                                <Box onClick={(e) => e.stopPropagation()}>
                                  <IconButton
                                    size="xs"
                                    variant="ghost"
                                    colorPalette="red"
                                    onClick={() => requestDeleteActuacion(a.id)}
                                    aria-label="Eliminar"><FaTrash /></IconButton>
                                </Box>
                              </HStack>
                            </Card.Body>
                          </Card.Root>
                        );
                      })}
                    </SimpleGrid>
                  ) : (
                    <Box overflowX="auto" rounded="xl" borderWidth="1px" borderColor={borderSubtle}>
                      <Table.Root size="sm" variant="simple">
                        <Table.Header bg={toolbarBg}>
                          <Table.Row>
                            <Table.ColumnHeader>Nombre</Table.ColumnHeader>
                            <Table.ColumnHeader>Fecha / hora</Table.ColumnHeader>
                            <Table.ColumnHeader display={{ base: 'none', md: 'table-cell' }}>Ubicación</Table.ColumnHeader>
                            <Table.ColumnHeader>Estado</Table.ColumnHeader>
                            <Table.ColumnHeader display={{ base: 'none', sm: 'table-cell' }}>Tipo</Table.ColumnHeader>
                            <Table.ColumnHeader textAlign='end'>Encuesta</Table.ColumnHeader>
                            <Table.ColumnHeader w="56px" />
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {displayedActivas.map((a) => {
                            const respuestas = a.encuesta?._count?.respuestas || 0;
                            return (
                              <Table.Row
                                key={a.id}
                                cursor="pointer"
                                _hover={{ bg: tableRowHoverBg }}
                                onClick={() => {
                                  setDetalleReadOnly(false);
                                  setSelectedId(a.id);
                                }}
                              >
                                <Table.Cell fontWeight="600" maxW={{ md: '220px' }} whiteSpace="normal">
                                  {a.nombre}
                                </Table.Cell>
                                <Table.Cell whiteSpace="nowrap">
                                  {a.fecha ? (
                                    <>
                                      <Icon mr={1} color="gray.500" asChild><FaCalendarAlt /></Icon>
                                      {new Date(a.fecha).toLocaleDateString()}
                                      {formatActuacionHorario(a) ? (
                                        <Text as="span" fontSize="xs" color="gray.500" display="block">
                                          {formatActuacionHorario(a)}
                                        </Text>
                                      ) : null}
                                    </>
                                  ) : (
                                    <Text color="gray.400" fontSize="xs">
                                      Sin fecha
                                    </Text>
                                  )}
                                </Table.Cell>
                                <Table.Cell display={{ base: 'none', md: 'table-cell' }} color="gray.600" maxW="200px" lineClamp={2}>
                                  {a.ubicacion || '—'}
                                </Table.Cell>
                                <Table.Cell>
                                  <Badge colorPalette={ESTADO_COLORS[a.estado]} fontSize="2xs">
                                    {ESTADO_LABELS[a.estado]}
                                  </Badge>
                                </Table.Cell>
                                <Table.Cell display={{ base: 'none', sm: 'table-cell' }}>
                                  <Badge colorPalette={colorTipoActuacion(a.tipo, tiposFormacionCatalogo)} fontSize="2xs">
                                    {labelTipoActuacion(a.tipo, tiposFormacionCatalogo)}
                                  </Badge>
                                </Table.Cell>
                                <Table.Cell fontSize="xs" color="gray.600" textAlign='end'>
                                  {a.encuesta ? `${respuestas}` : '—'}
                                </Table.Cell>
                                <Table.Cell onClick={(e) => e.stopPropagation()}>
                                  <IconButton
                                    size="xs"
                                    variant="ghost"
                                    colorPalette="red"
                                    onClick={() => requestDeleteActuacion(a.id)}
                                    aria-label="Eliminar"><FaTrash /></IconButton>
                                </Table.Cell>
                              </Table.Row>
                            );
                          })}
                        </Table.Body>
                      </Table.Root>
                    </Box>
                  )}
                </TabPanel>
                <TabPanel value="1" px={0} pt={4}>
                  {actuacionesHistorico.length === 0 ? (
                    <Card.Root bg={cardBg} shadow="md" rounded="xl" borderWidth="1px" borderColor={borderSubtle}>
                      <Card.Body py={10} textAlign="center">
                        <Icon boxSize={10} color="gray.400" mb={3} asChild><FaHistory /></Icon>
                        <Heading size="sm" color="gray.600" mb={2}>
                          Aún no hay actuaciones en el histórico
                        </Heading>
                        <Text fontSize="sm" color="gray.500">
                          Cuando una actuación tenga fecha y su hora de fin (o el día completo si no indicaste hora de
                          fin) sea anterior a ahora, aparecerá aquí de forma automática.
                        </Text>
                      </Card.Body>
                    </Card.Root>
                  ) : actuacionesHistoricoFiltradas.length === 0 ? (
                    <Card.Root bg={cardBg} shadow="md" rounded="xl" borderWidth="1px" borderColor={borderSubtle}>
                      <Card.Body textAlign="center" py={10}>
                        <Icon boxSize={10} color="gray.400" mb={3} asChild><FaFilter /></Icon>
                        <Heading size="sm" color="gray.600" mb={2}>
                          Ninguna actuación coincide con los filtros
                        </Heading>
                        <Text fontSize="sm" color="gray.500" mb={4}>
                          Ajusta el nombre, las fechas o el orden, o limpia los filtros.
                        </Text>
                        <Button size="sm" variant="outline" onClick={clearListFilters}><FaTimes />Limpiar filtros
                                                  </Button>
                      </Card.Body>
                    </Card.Root>
                  ) : (
                    <Box overflowX="auto" rounded="xl" borderWidth="1px" borderColor={borderSubtle}>
                      <Table.Root size="sm" variant="simple">
                        <Table.Header bg={toolbarBg}>
                          <Table.Row>
                            <Table.ColumnHeader>Nombre</Table.ColumnHeader>
                            <Table.ColumnHeader>Fecha / hora</Table.ColumnHeader>
                            <Table.ColumnHeader display={{ base: 'none', md: 'table-cell' }}>Ubicación</Table.ColumnHeader>
                            <Table.ColumnHeader>Estado</Table.ColumnHeader>
                            <Table.ColumnHeader display={{ base: 'none', sm: 'table-cell' }}>Tipo</Table.ColumnHeader>
                            <Table.ColumnHeader textAlign='end'>Encuesta</Table.ColumnHeader>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {actuacionesHistoricoFiltradas.map((a) => {
                            const respuestas = a.encuesta?._count?.respuestas || 0;
                            return (
                              <Table.Row
                                key={a.id}
                                cursor="pointer"
                                _hover={{ bg: tableRowHoverBg }}
                                onClick={() => {
                                  setDetalleReadOnly(true);
                                  setSelectedId(a.id);
                                }}
                              >
                                <Table.Cell fontWeight="600" maxW={{ md: '220px' }} whiteSpace="normal">
                                  {a.nombre}
                                </Table.Cell>
                                <Table.Cell whiteSpace="nowrap">
                                  {a.fecha ? (
                                    <>
                                      <Icon mr={1} color="gray.500" asChild><FaCalendarAlt /></Icon>
                                      {new Date(a.fecha).toLocaleDateString()}
                                      {formatActuacionHorario(a) ? (
                                        <Text as="span" fontSize="xs" color="gray.500" display="block">
                                          {formatActuacionHorario(a)}
                                        </Text>
                                      ) : null}
                                    </>
                                  ) : (
                                    <Text color="gray.400" fontSize="xs">
                                      Sin fecha
                                    </Text>
                                  )}
                                </Table.Cell>
                                <Table.Cell display={{ base: 'none', md: 'table-cell' }} color="gray.600" maxW="200px" lineClamp={2}>
                                  {a.ubicacion || '—'}
                                </Table.Cell>
                                <Table.Cell>
                                  <Badge colorPalette={ESTADO_COLORS[a.estado]} fontSize="2xs">
                                    {ESTADO_LABELS[a.estado]}
                                  </Badge>
                                </Table.Cell>
                                <Table.Cell display={{ base: 'none', sm: 'table-cell' }}>
                                  <Badge colorPalette={colorTipoActuacion(a.tipo, tiposFormacionCatalogo)} fontSize="2xs">
                                    {labelTipoActuacion(a.tipo, tiposFormacionCatalogo)}
                                  </Badge>
                                </Table.Cell>
                                <Table.Cell fontSize="xs" color="gray.600" textAlign='end'>
                                  {a.encuesta ? `${respuestas}` : '—'}
                                </Table.Cell>
                              </Table.Row>
                            );
                          })}
                        </Table.Body>
                      </Table.Root>
                    </Box>
                  )}
                </TabPanel>
              </TabPanels>
            </Tabs.Root>
          )}
        </VStack>
      </Box>

      <ActuacionCreateWizard
        isOpen={isOpen}
        onClose={onClose}
        form={form}
        setForm={setForm}
        plantillas={plantillas}
        ubicLlegadaDistinta={ubicLlegadaDistinta}
        setUbicLlegadaDistinta={setUbicLlegadaDistinta}
        createFormKey={createFormKey}
        fechaMinYMD={localDateYMD()}
        toast={toast}
        onCreated={handleCreateSuccess}
      />

      <Dialog.Root
        open={isDeleteOpen}
        initialFocusEl={() => cancelDeleteRef.current}
        placement='center'
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            onDeleteClose();
          }
        }}>
        <Portal>

          <Dialog.Backdrop>
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header fontSize="lg" fontWeight="bold">
                  Eliminar actuación
                </Dialog.Header>
                <Dialog.Body>
                  ¿Seguro que quieres eliminar{' '}
                  <strong>{actuacionPendienteEliminar?.nombre || 'esta actuación'}</strong>?
                  Esta acción no se puede deshacer.
                </Dialog.Body>
                <Dialog.Footer gap={3}>
                  <Button ref={cancelDeleteRef} onClick={onDeleteClose} disabled={deletingActuacion}>
                    Cancelar
                  </Button>
                  <Button
                    colorPalette="red"
                    onClick={confirmDeleteActuacion}
                    loading={deletingActuacion}
                  >
                    Eliminar
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Backdrop>

        </Portal>
</Dialog.Root>
    </AppLayout>
  );
}
